import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const themes = {
  light: {
    name: 'light',
    primaryColor: '#7D010C',
    primaryColorTranslucent: 'rgba(125,1,12,0.45)',
    secColor1: '#7b7189',
    secColor2: '#f2ebe9',
    secColor3: '#8f1420',
    secColor4: '#241a2a',
    secColor5: '#c0303e',
    appBg: '#faf7f6',
    cardBg: '#ffffff',
    cardSolid: '#ffffff',
    modalBg: '#ffffff',
    modalMask: 'rgba(20, 10, 12, 0.55)',
    borderColor: 'rgba(125,20,30,0.12)',
    textPrimary: '#241a2a',
    textSecondary: '#463c54',
    shadow: '0 18px 44px rgba(125,20,30,0.11)',
    hoverBg: 'rgba(60,25,35,0.05)',
    tableHeaderBg: 'rgba(125,20,30,0.06)',
    tableRowBg: 'rgba(255,255,255,0.7)',
    tableBorder: 'rgba(125,20,30,0.10)',
  },
  dark: {
    name: 'dark',
    primaryColor: '#7D010C',
    primaryColorTranslucent: 'rgba(125,1,12,0.45)',
    secColor1: '#8f8aa6',
    secColor2: '#120808',
    secColor3: '#c0303e',
    secColor4: '#ecebf4',
    secColor5: '#c0303e',
    appBg: '#090506',
    cardBg: '#1a1010',
    cardSolid: '#1a1010',
    modalBg: '#1a0e0e',
    modalMask: 'rgba(0, 0, 0, 0.72)',
    borderColor: 'rgba(255,255,255,0.10)',
    textPrimary: '#ecebf4',
    textSecondary: '#cfcbe2',
    shadow: '0 18px 44px rgba(0,0,0,0.40)',
    hoverBg: 'rgba(255,255,255,0.06)',
    tableHeaderBg: 'rgba(255,255,255,0.04)',
    tableRowBg: 'rgba(255,255,255,0.02)',
    tableBorder: 'rgba(255,255,255,0.08)',
  },
};

const readInitialTheme = () => {
  try {
    const cz = localStorage.getItem('cz-theme');
    if (cz === 'light' || cz === 'dark') return cz;
    const legacy = localStorage.getItem('chainz-theme');
    if (legacy === 'light' || legacy === 'dark') return legacy;
  } catch {
    /* ignore */
  }
  return 'dark';
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(readInitialTheme);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const theme = themes[currentTheme];
    const root = document.documentElement;
    // Set on <html> so MUI/Ant portals (dialogs outside .cz-root) still resolve tokens.
    Object.entries({
      '--primaryColor': theme.primaryColor,
      '--primaryColorTranslucent': theme.primaryColorTranslucent,
      '--secColor1': theme.secColor1,
      '--secColor2': theme.secColor2,
      '--secColor3': theme.secColor3,
      '--secColor4': theme.secColor4,
      '--secColor5': theme.secColor5,
      '--appBg': theme.appBg,
      '--cardBg': theme.cardBg,
      '--card-solid': theme.cardSolid,
      '--modal-bg': theme.modalBg,
      '--modal-mask': theme.modalMask,
      '--tx': theme.textPrimary,
      '--tx2': theme.textSecondary,
      '--borderColor': theme.borderColor,
      '--textPrimary': theme.textPrimary,
      '--textSecondary': theme.textSecondary,
      '--shadow': theme.shadow,
      '--hoverBg': theme.hoverBg,
      '--tableHeaderBg': theme.tableHeaderBg,
      '--tableRowBg': theme.tableRowBg,
      '--tableBorder': theme.tableBorder,
    }).forEach(([k, v]) => root.style.setProperty(k, v));

    root.classList.toggle('lite', currentTheme === 'light');
    document.body.className = `theme-${currentTheme}`;
    try {
      localStorage.setItem('cz-theme', currentTheme);
      localStorage.setItem('chainz-theme', currentTheme);
    } catch {
      /* ignore */
    }
  }, [currentTheme]);

  const toggleTheme = () => {
    setCurrentTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setTheme = (themeName) => {
    if (themes[themeName]) setCurrentTheme(themeName);
  };

  const value = {
    currentTheme,
    theme: themes[currentTheme],
    themes,
    toggleTheme,
    setTheme,
    isLoading,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
